import Layout from '@/components/layout/Layout';
import { Target, Eye, Award, Users, BookOpen, TrendingUp } from 'lucide-react';
import founderImg from "/src/assets/about/founder_final.png";

const About = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background pt-8 pb-12">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[120px]" />
        </div>

        <div className="container-max text-center relative z-10">
          <span className="inline-block px-4 py-2 bg-accent/5 border border-accent/20 rounded-full text-accent-foreground text-[10px] font-black uppercase tracking-[0.2em] mb-8">
            Our DNA
          </span>
          <h1 className="text-2xl md:text-4xl font-black text-primary mb-6 tracking-tighter uppercase">
            Engineering Financial<br />
            <span className="text-gradient-gold">Sovereignty</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto font-medium leading-relaxed">
            Ratchagan Analysis is the premier destination for institutional-grade stock market education and professional mentorship.
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section className="section-padding bg-secondary/10">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="flex justify-center lg:justify-start">
              <div className="relative">
                {/* Subtle Premium Shadow */}
                <div className="absolute -inset-4 bg-primary/5 blur-3xl rounded-full opacity-50" />

                <img
                  src={founderImg}
                  alt="Founder - Ratchagan Analysis"
                  className="relative z-10 w-[280px] lg:w-[420px] object-contain rounded-3xl shadow-xl transition-transform duration-700 hover:scale-[1.02]"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/5 rounded-full text-[10px] font-black uppercase tracking-widest text-primary">
                Executive Leadership
              </div>
              <h2 className="text-3xl font-black text-gradient-gold uppercase tracking-tighter leading-none">
                Founder / NISM research analyst
              </h2>

              <div className="w-20 h-1.5 bg-gradient-to-r from-accent to-accent-light rounded-full" />

              <p className="text-lg text-muted-foreground font-medium leading-relaxed">
                Ratchagan Analysis is led by a <strong>NISM certified research analyst</strong> with <strong>5+ years of experience</strong>. We deliver data-driven stock market insights with absolute clarity and institutional discipline.
              </p>

              <div className="p-10 rounded-[2rem] bg-white/60 border border-white/60 backdrop-blur-xl shadow-premium">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Award className="w-4 h-4 text-accent" />
                  </div>
                  <div>
                    <h4 className="text-lg font-black text-primary uppercase tracking-tight">NISM Certification</h4>
                    <p className="text-sm font-medium text-muted-foreground mt-1">
                      Ensuring the highest standards of financial research and ethical trading practices.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/3 blur-[120px] pointer-events-none" />

        <div className="container-max relative z-10">
          <div className="grid md:grid-cols-2 gap-10">
            <div className="card-premium group relative bg-white/40 overflow-hidden border-white/60 p-12">
              <div className="w-16 h-16 rounded-2xl bg-primary shadow-soft flex items-center justify-center mb-10 group-hover:scale-110 transition-transform duration-500">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-black text-primary mb-6 uppercase tracking-tighter">Mission</h3>
              <p className="text-muted-foreground font-medium leading-relaxed">
                To provide simple and practical stock market education. We help everyone understand how to trade with discipline and focus on financial growth.
              </p>

              <div className="absolute -bottom-6 -right-6 opacity-[0.03] rotate-12">
                <Target className="w-40 h-40 text-primary" />
              </div>
            </div>

            <div className="card-premium group relative bg-white/40 overflow-hidden border-white/60 p-12">
              <div className="w-16 h-16 rounded-2xl bg-accent shadow-soft flex items-center justify-center mb-10 group-hover:scale-110 transition-transform duration-500">
                <Eye className="w-8 h-8 text-accent-foreground" />
              </div>
              <h3 className="text-2xl font-black text-primary mb-6 uppercase tracking-tighter">Vision</h3>
              <p className="text-muted-foreground font-medium leading-relaxed">
                To build a strong community of traders who can read markets clearly and achieve their financial goals through smart trading.
              </p>

              <div className="absolute -bottom-6 -right-6 opacity-[0.03] rotate-12">
                <Eye className="w-40 h-40 text-accent" />
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* What We Offer */}
      <section className="section-padding bg-background relative overflow-hidden">
        <div className="container-max relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="inline-block px-4 py-2 bg-primary/5 border border-primary/10 rounded-full text-primary text-[10px] font-black uppercase tracking-[0.2em] mb-6">
              Core Capabilities
            </span>
            <h2 className="heading-lg text-primary mb-6">
              The Academic <span className="text-gradient-gold">Framework</span>
            </h2>
            <p className="text-lg text-muted-foreground font-medium">
              A comprehensive educational ecosystem designed to scale your trading proficiency from foundation to professional optimization.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-10">
            {[
              { icon: BookOpen, title: 'Structured Curriculum', description: 'Institutional learning path from basic mechanics to high-frequency strategies.' },
              { icon: TrendingUp, title: 'Live Market Intelligence', description: 'Real-time decoding of market dynamics and institutional order flow.' },
            ].map((item, index) => (
              <div key={index} className="card-premium group bg-white/40 border-white/60 p-10 hover:bg-white">
                <div className="w-14 h-14 rounded-2xl bg-white shadow-soft flex items-center justify-center mb-8 border border-gray-100 group-hover:bg-accent transition-all duration-500">
                  <item.icon className="w-7 h-7 text-primary group-hover:text-accent-foreground transition-colors duration-500" />
                </div>
                <h3 className="text-lg font-black text-primary mb-4 uppercase tracking-tighter">{item.title}</h3>
                <p className="text-sm font-medium text-muted-foreground leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default About;
